import axios from 'axios';
import { parseProblem } from '@shared/api-errors';
import { getCorrelationId } from '../lib/correlation';
import { logger } from '../lib/logger';

const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001';
const api = axios.create({
  baseURL: baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add tenant slug, auth token, and correlation ID to requests
api.interceptors.request.use((config) => {
  const tenantSlug = localStorage.getItem('portal_tenant_slug');
  if (tenantSlug) {
    config.headers['X-Tenant-Slug'] = tenantSlug;
  }

  const token = localStorage.getItem('portal_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  config.headers['X-Correlation-ID'] = getCorrelationId();

  return config;
});

// Shared refresh promise to prevent concurrent refresh calls
let refreshPromise: Promise<string> | null = null;

function clearPortalAuthAndRedirect() {
  localStorage.removeItem('portal_access_token');
  localStorage.removeItem('portal_refresh_token');
  localStorage.removeItem('portal_contact');
  window.location.href = '/login';
}

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('portal_refresh_token');
      if (!refreshToken) {
        clearPortalAuthAndRedirect();
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(
              `${api.defaults.baseURL}/portal/auth/refresh-token`,
              { refreshToken },
              {
                headers: {
                  'X-Tenant-Slug': localStorage.getItem('portal_tenant_slug') || '',
                },
              }
            )
            .then((res) => {
              const { accessToken, refreshToken: newRefreshToken } = res.data;
              localStorage.setItem('portal_access_token', accessToken);
              localStorage.setItem('portal_refresh_token', newRefreshToken);
              return accessToken;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        const accessToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        clearPortalAuthAndRedirect();
        return Promise.reject(error);
      }
    }

    // Normalize to ApiError when the server sent problem+json (or a legacy
    // {message,errors} 4xx shape — see parseProblem fallback). Downstream
    // consumers (toastFromApiError, mapProblemToFormErrors, mutationCache
    // onError) get a uniform shape regardless of the backend endpoint's
    // migration status.
    const apiErr = parseProblem(error);
    if (apiErr) return Promise.reject(apiErr);

    logger.warn('API request failed', {
      url: error.config?.url,
      status: error.response?.status,
      correlationId: getCorrelationId(),
    });

    return Promise.reject(error);
  }
);

export const getTenantSlug = (): string => {
  return localStorage.getItem('portal_tenant_slug') || 'demo';
};

export { api };
export default api;
